import { type ReactNode, useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import SV from 'server-vars';
import { FormattedMessage } from 'dibs-react-intl';
import { PAGE_TYPE, BROWSE_TYPES } from 'dibs-constants/exports/pageTypes';
import { useAbTestV2BuyerUser } from 'dibs-ab-tests/exports/clientAbTestV2';
import { trackEvent } from 'dibs-tracking';
import { useSupportChatVisibility } from 'dibs-support-chat/exports/useSupportChatVisibility';

import { isLowResolution } from '../MessageBanner/helpers/sharedMessageHelpers';

import { type useOfferVisibility_viewer$key } from './__generated__/useOfferVisibility_viewer.graphql';

const AB_TEST_NAME = 'offer-visibility-indicator';
const IS_ALLOWED_PAGE_TYPE = isLowResolution
    ? // if mobile, follow marketing banner logic and only show on browse, pdp, and homepage
      [...BROWSE_TYPES, PAGE_TYPE.PDP, PAGE_TYPE.HOME].includes(SV.get('settings.pageType'))
    : // if desktop, show on all pages except checkout
      SV.get('settings.pageType') !== PAGE_TYPE.CHECKOUT;

const trackClick = (): void =>
    trackEvent({
        eventName: 'navigation',
        step_interaction_name: 'click',
        interaction_type: 'global nav',
        trigger: 'offer indicator clicked',
    });

const trackImpression = (variant: string, orderId: string): void =>
    trackEvent({
        eventName: 'navigation',
        step_interaction_name: 'impression',
        interaction_type: 'global nav',
        trigger: `offer indicator impression ${variant}`,
        orderId,
    });

export const useOfferVisibility = (
    viewerRef: useOfferVisibility_viewer$key | null,
    targetDevice: 'mobile' | 'desktop'
): {
    copy: ReactNode;
    href: string;
    onClick: () => void;
} | null => {
    const isSupportChatVisible = useSupportChatVisibility();
    const trackedRef = useRef(false);
    const viewer = useFragment(
        graphql`
            fragment useOfferVisibility_viewer on Viewer {
                conversationsSolr(buyerId: $userId, buyerTags: "activePrivateOffer")
                    @include(if: $hasUserId) {
                    edges {
                        node {
                            transaction {
                                serviceId
                                transactionType
                            }
                            item {
                                serviceId
                            }
                        }
                    }
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...clientAbTestV2_useAbTestV2BuyerUser_user
                }
                offerVisibilityFF: featureFlag(feature: "offerVisibilityIndicator")
            }
        `,
        viewerRef
    );

    const activeOffers = viewer?.conversationsSolr?.edges;
    const { item, transaction } = activeOffers?.[0]?.node || {};
    const singleOrderPath =
        transaction?.transactionType === 'NEGOTIABLE'
            ? `/orders/order/${transaction?.serviceId}/`
            : `/checkout/id-${item?.serviceId}/`;
    const enabled =
        !!viewer?.offerVisibilityFF &&
        IS_ALLOWED_PAGE_TYPE &&
        !!activeOffers?.length &&
        (targetDevice === 'mobile' ? isLowResolution && !isSupportChatVisible : !isLowResolution);
    const abTest = useAbTestV2BuyerUser({
        testName: AB_TEST_NAME,
        user: viewer?.user,
        shouldFireTracking: enabled,
    });

    useEffect(() => {
        if (enabled && abTest?.variant && !trackedRef.current) {
            trackedRef.current = true;
            trackImpression(
                abTest.variant,
                activeOffers.map(edge => edge?.node?.transaction?.serviceId).join(', ')
            );
        }
    }, [abTest?.variant, activeOffers, enabled]);

    return enabled && abTest?.variant === 'variant'
        ? {
              copy: (
                  <FormattedMessage
                      id="dbl.Header.OfferVisibility.offer"
                      defaultMessage="You have {activeOffers, plural, one {an offer} other {# offers}}"
                      values={{ activeOffers: activeOffers.length }}
                  />
              ),
              href:
                  activeOffers.length === 1 ? singleOrderPath : '/inbox/?filter=activePrivateOffer',
              onClick: trackClick,
          }
        : null;
};
